// src/components/resources/calculators/video-dropoff/components/BreakpointsTable.tsx

/**
 * @file Breakpoints Table Component
 * @description Table component for displaying and editing video breakpoints
 */

import RiSubtractLine from "~/icons/remix/generated/RiSubtractLine"
import type { Breakpoint } from '~/components/resources/calculators/video-dropoff/types'

/**
 * Props for the BreakpointsTable component
 */
interface BreakpointsTableProps {
  /** Array of breakpoints to display */
  breakpoints: Breakpoint[]
  /** Video length in seconds for time calculations */
  videoLength: number
  /** Total views for validation */
  totalViews: number
  /** Whether custom platform is selected (shows remove buttons) */
  isCustomPlatform: boolean
  /** Callback when breakpoint view count is updated */
  onUpdateBreakpointViewCount: (id: string, newViewCount: number) => void
  /** Callback when breakpoints are validated */
  onValidateBreakpoints: () => void
  /** Callback when breakpoint is removed */
  onRemoveBreakpoint: (id: string) => void
  /** Function to format time display */
  formatTime: (seconds: number) => string
}

/**
 * Breakpoints table component for displaying and editing breakpoints
 * @param {BreakpointsTableProps} props - Component props
 * @returns {JSX.Element} Rendered breakpoints table
 */
export const BreakpointsTable = ({
  breakpoints,
  videoLength,
  totalViews,
  isCustomPlatform,
  onUpdateBreakpointViewCount,
  onValidateBreakpoints,
  onRemoveBreakpoint,
  formatTime
}: BreakpointsTableProps): JSX.Element => {
  if (breakpoints.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No breakpoints defined. Select a platform or add custom breakpoints.
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Breakpoint
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Time
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Viewers
            </th>
            {isCustomPlatform && (
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
          {breakpoints.map((breakpoint, index) => {
            // Calculate actual time if percentile
            const timeInSeconds = breakpoint.isPercentile 
              ? (breakpoint.value / 100) * videoLength
              : breakpoint.value
            
            // Format time using the provided formatter
            const formattedTime = formatTime(timeInSeconds)
            
            return (
              <tr key={breakpoint.id} className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {breakpoint.label}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  {formattedTime}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                  <input
                    type="number"
                    value={breakpoint.viewCount}
                    onChange={(e) => onUpdateBreakpointViewCount(breakpoint.id, +e.target.value)}
                    onBlur={onValidateBreakpoints}
                    min={0}
                    max={totalViews}
                    className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800"
                    disabled={breakpoint.id === 'start'} // Disable editing for the start breakpoint
                  />
                </td>
                {isCustomPlatform && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {breakpoint.id !== 'start' && (
                      <button
                        type="button"
                        onClick={() => onRemoveBreakpoint(breakpoint.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <RiSubtractLine className="h-4 w-4" />
                        <span className="sr-only">Remove</span>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
